import React from 'react';
import { motion } from 'framer-motion';
import { Clock, Play, Trophy, Lock, Sparkles } from 'lucide-react';
import type { ModeMetadata } from '../modes';
import { CardSurface } from './shared/CardSurface';
import { getModeVisuals } from './shared/modeTheme';
import { MasteryProgressBar } from './shared/MasteryProgressBar';
interface LabStationProps {
  mode: ModeMetadata;
  personalNote?: string;
  masteryPercent?: number;
  statLabel?: string;
  personalRecord?: string;
  /** When true, renders a subtle pulse-ring indicating new content (F-UI-4). */
  newlyUnlocked?: boolean;
  /**
   * Waarom deze modus nog niet zinvol is VOOR DIT KIND (bijv. "Oefen eerst een
   * onderwerp"). Gevuld betekent: de modus bestaat en werkt, maar er is nog
   * niets om mee te werken.
   *
   * Bewust een eigen toestand naast `mode.isEnabled`. Die laatste betekent
   * "deze modus bestaat nog niet in het product" en toont een hangslot met
   * "Binnenkort". Die twee samenvoegen zou een kind laten lezen dat Uitdaging
   * er nooit komt, terwijl het er morgen kan zijn — het hoeft er alleen zelf
   * iets voor te doen. Dat verschil is het hele punt.
   */
  notYetReason?: string;
  /**
   * `primary` — volledige kaart, voor wat het kind NU kan doen.
   * `compact` — lichte rij, voor wat er nog niet inzit ("Oefen eerst een
   * onderwerp") of nog niet bestaat ("Binnenkort").
   *
   * Vier gelijke kleurvlakken lieten "je kunt hier niets mee" er even
   * uitnodigend uitzien als "begin hier". De vorm zegt nu wat de staat is, en
   * dat scheelt een kind het openen van een lege lijst om erachter te komen.
   */
  variant?: 'primary' | 'compact';
  onClick: () => void;
}
const ctaTextMap: Record<string, string> = {
  fluency: 'Train snelheid',
  sprint: 'Start sprint',
  challenge: 'Accepteer uitdaging',
  free: 'Kies onderwerp'
};
/**
 * LabStation — Interactive card for a single practice mode.
 *
 * Audit (Learning Lab v1.1):
 * - F-UI-3: Djay-bubble is verwijderd uit de card — Djay blijft in de page-hero
 *   en MissionBriefing zodat de mascotte zijn zeldzaamheid houdt.
 * - F-UI-4: Subtle pulse-ring wanneer `newlyUnlocked` actief is.
 * - F-FEEL-6: Disabled-state krijgt subtle tremor + slot-icoon ipv stilte.
 * - F-UI-5: Flattened DOM structure using CardSurface primitive.
 *
 * Part of the learningLab domain (DDD co-locatie s57).
 */
export function LabStation({
  mode,
  personalNote,
  masteryPercent,
  statLabel,
  personalRecord,
  newlyUnlocked = false,
  notYetReason,
  variant = 'primary',
  onClick
}: LabStationProps) {
  const {
    Icon,
    theme: colors
  } = getModeVisuals(mode.id);
  const isDisabled = !mode.isEnabled;
  // Nog niets om mee te werken, maar de modus bestaat wel: gedempt en
  // aanklikbaar. Aanklikbaar met opzet — de kiezer erachter legt zelf uit wat
  // er nodig is, en een kaart die niets doet is voor een kind niet te
  // onderscheiden van een kapotte kaart.
  const isNotYet = !isDisabled && !!notYetReason;
  const ctaText = ctaTextMap[mode.id] || 'Start';
  if (variant === 'compact') {
    return <CardSurface isDisabled={isDisabled} onClick={onClick} className={`w-full flex items-center gap-3 p-4 rounded-2xl border-2 border-slate-200 bg-white text-left transition-colors ${isDisabled ? 'opacity-70 cursor-not-allowed' : 'hover:border-slate-300 cursor-pointer'}`} data-id="element-3021" data-mp="14dp963rwyp6v">
        
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${isDisabled ? 'bg-slate-100' : colors.bgSoft}`} data-id="element-3022" data-mp="qg0kekuq6xwj">
          
          <Icon className={`w-5 h-5 ${isDisabled ? 'text-slate-400' : colors.text}`} strokeWidth={2.5} data-id="element-3023" data-mp="gq7arpe58sux" />
          
        </div>
        <div className="flex-1 min-w-0" data-id="element-3024" data-mp="brxetcklho89">
          <div className="text-sm font-black text-slate-800 leading-tight" data-id="element-3025" data-mp="6sb5vfbsuu59">
            {mode.title}
          </div>
          <div className="text-xs font-medium text-slate-500 mt-0.5" data-id="element-3026" data-mp="2r7lqgmpyby8">
            {isDisabled ? 'Binnenkort beschikbaar' : notYetReason}
          </div>
        </div>
        {isDisabled ? <Lock className="w-4 h-4 text-slate-400 flex-shrink-0" data-id="element-3027" data-mp="1xtuivvxqn08w" /> : <span className="text-xs font-bold text-slate-400 flex-shrink-0" data-id="element-3028" data-mp="1xue3jkerw2qm">
            Bekijk
          </span>}
      </CardSurface>;
  }
  return <CardSurface isDisabled={isDisabled} onClick={onClick} className={`h-full w-full flex flex-col p-6 rounded-[2rem] transition-colors overflow-hidden group ${isDisabled ? 'bg-slate-100 opacity-70 cursor-not-allowed text-slate-500' : `bg-gradient-to-br ${colors.gradient} cursor-pointer hover:shadow-xl shadow-lg text-white${isNotYet ? ' opacity-80' : ''}`} ${newlyUnlocked && !isDisabled ? `ring-4 ring-offset-2 ring-offset-white ${colors.ring}` : ''}`} data-id="element-3029" data-mp="1o42mqx9zpz2b">
      
      {/* F-UI-4: pulse-ring overlay for newly-unlocked content */}
      {newlyUnlocked && !isDisabled && <motion.div aria-hidden className={`absolute inset-0 rounded-[2rem] ring-4 ${colors.ring} pointer-events-none`} animate={{
      opacity: [0.6, 0, 0.6],
      scale: [1, 1.04, 1]
    }} transition={{
      duration: 2,
      repeat: Infinity,
      ease: 'easeInOut'
    }} data-id="element-3030" data-mp="1a4jtfalu8sa2" />}

      {/* F-UI-4: "Nieuw!" badge top-right when newly unlocked */}
      {newlyUnlocked && !isDisabled && <div className="absolute top-3 right-3 z-20 flex items-center gap-1 px-2 py-1 rounded-full bg-white text-[10px] font-black uppercase tracking-wider shadow-md" data-id="element-3031" data-mp="39og0w5h77cg0">
          <Sparkles className={`w-3 h-3 ${colors.text}`} data-id="element-3032" data-mp="3inb9fo7psymh" />
          <span className={colors.text} data-id="element-3033" data-mp="3ilqg21x4zlq7">Nieuw!</span>
        </div>}

      {/* Top Row: Icon & Time */}
      <div className="flex items-start justify-between mb-4 relative z-10 w-full" data-id="element-3034" data-mp="19ftuzd7n4ks">
        <motion.div whileHover={!isDisabled ? {
        rotate: [0, -10, 10, -10, 0]
      } : {}} transition={{
        duration: 0.5
      }} className={`w-14 h-14 rounded-2xl flex items-center justify-center backdrop-blur-sm relative
            ${isDisabled ? 'bg-slate-200' : 'bg-white/20 shadow-inner'}`} data-id="element-3035" data-mp="3sdee4lo7810n">
          
          <Icon className={`w-7 h-7 ${isDisabled ? 'text-slate-400' : 'text-white'}`} strokeWidth={2.5} data-id="element-3036" data-mp="2c17l8r3voxlx" />
          
          {isDisabled && <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-slate-300 flex items-center justify-center shadow-sm" data-id="element-3037" data-mp="axyg1t78w0pd">
              <Lock className="w-3 h-3 text-slate-600" strokeWidth={3} data-id="element-3038" data-mp="fwrqtdv5ddlu" />
            </div>}
        </motion.div>

        <div className={`flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full backdrop-blur-sm
          ${isDisabled ? 'bg-slate-200 text-slate-500' : 'bg-white/20 text-white'}`} data-id="element-3039" data-mp="kxfje3z0y2i6">
          
          <Clock className="w-4 h-4" data-id="element-3040" data-mp="tu6o14rn6pod" />
          <span data-id="element-3041" data-mp="yuc7vyro77vw">{mode.estimatedMinutes}m</span>
        </div>
      </div>

      {/* Title & Subtitle */}
      <div className="mb-4 flex-1 relative z-10 w-full" data-id="element-3042" data-mp="13lkj90ck93bp">
        <h3 className="text-2xl font-black tracking-tight leading-tight mb-1" data-id="element-3043" data-mp="13o6ouufw58ra">
          {mode.title}
        </h3>
        <p className={`text-sm font-medium line-clamp-3 ${isDisabled ? 'text-slate-400' : 'text-white/90'}`} data-id="element-3044" data-mp="1dk078ugajwgz">
          
          {mode.description}
        </p>
        {/* De voorwaarde staat op de KAART, niet pas nadat het kind hem heeft
             geopend. Anders is de enige manier om te ontdekken dat er nog niets
             is, het openen van een lege lijst. */}
        {isNotYet && <div className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-black/15 px-2 py-1 text-xs font-bold text-white" data-id="element-3045" data-mp="2y4f5801845xq">
            <Lock className="h-3.5 w-3.5" aria-hidden="true" data-id="element-3046" data-mp="2o4eh80set1n7" />
            {notYetReason}
          </div>}
        {personalRecord && !isDisabled && <div className="mt-3 inline-flex items-center gap-1.5 px-2 py-1 rounded-lg bg-white/10 text-xs font-bold text-white/90" data-id="element-3047" data-mp="3lr3uj6skb0od">
            <Trophy className="w-3.5 h-3.5" data-id="element-3048" data-mp="3guwmn8glvf3s" />
            {personalRecord}
          </div>}
      </div>

      {/* Progress Bar — zie shared/MasteryProgressBar: label in plaats van
           percentage, en de progressbar-semantiek zit in dat component zodat de
           drie andere kiezers hem niet opnieuw kunnen vergeten. */}
      {masteryPercent !== undefined && !isDisabled && <div className="mb-4 relative z-10 w-full" data-id="element-3049" data-mp="3kpgz8y7gsqi">
          <MasteryProgressBar masteryScore={masteryPercent} caption="Voortgang" tone="onColor" data-id="element-3050" data-mp="383bm9jdb481j" />
        
        </div>}

      {/* Action Area */}
      <div className={`pt-4 flex items-center justify-between border-t relative z-10 w-full ${isDisabled ? 'border-slate-200' : 'border-white/20'}`} data-id="element-3051" data-mp="3cvuxwasfot3p">
        
        <div className={`text-xs font-bold ${isDisabled ? 'text-slate-400' : 'text-white/90'} line-clamp-1 pr-2`} data-id="element-3052" data-mp="333yzeymfr0yy">
          
          {statLabel || personalNote || mode.tags[0]}
        </div>

        {!isDisabled ? <div className={`px-4 py-2 rounded-xl bg-white text-sm font-black flex items-center gap-2 transition-transform group-hover:scale-105 shadow-sm ${colors.text}`} data-id="element-3053" data-mp="3vscwnc5p9t9g">
          
            {ctaText}
            <Play className="w-4 h-4" fill="currentColor" data-id="element-3054" data-mp="3mrepikzyt5mn" />
          </div> : <span className="text-xs font-black bg-slate-200 text-slate-400 px-4 py-2 rounded-xl inline-flex items-center gap-1.5" data-id="element-3055" data-mp="26dyc741pssjr">
            <Lock className="w-3.5 h-3.5" data-id="element-3056" data-mp="2becahudiko4l" />
            Binnenkort
          </span>}
      </div>
    </CardSurface>;
}